import React from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderText, SectionContainer, SectionSubtitle } from '../../../components/Modals';
import { Flex } from '../../../components/ui/Flex';
import ToggleInput from '../../../components/ui/ToggleInput';
import { isICTLockProvider, LockProviders } from '../../../utils/lockInfo';

// Lock providers whose access lists api-gratafy syncs to Salto automatically
// (salto.service.ts, EnableAutoSaltoSyncACL).
const SALTO_ACL_LOCK_PROVIDERS = [LockProviders.SALTO, LockProviders.SALTO_ALFRED, LockProviders.ICT_SALTO_ALFRED];

// SyncConfigFormInputs edits the building's sync_config (api-admin SynchronizationConfig).
// Super admins only: the building form renders it for them and api-admin drops sync_config
// from anyone else. Each switch shows where it applies (the org's PMS, the building's lock
// provider); a hidden switch keeps its loaded value.
const SyncConfigFormInputs = ({ form, isYardi, isEntrata, lockProvider }) => {
  const { t } = useTranslation();
  const showGxAcl = isICTLockProvider(lockProvider);
  const showSaltoAcl = SALTO_ACL_LOCK_PROVIDERS.includes(Number(lockProvider));

  if (!isYardi && !isEntrata && !showGxAcl && !showSaltoAcl) {
    return null;
  }

  return (
    <SectionContainer>
      <HeaderText alt>{t('Sync Settings')}</HeaderText>
      <SectionSubtitle>{t('How this building syncs with its PMS and lock system.')}</SectionSubtitle>
      <Flex direction="column" gap="16px">
        {isYardi && (
          <>
            <ToggleInput
              form={form}
              name="sync_config.enable_yardi_users"
              label={t('Sync Residents from Yardi')}
              description={t('Include this building in the scheduled Yardi resident sync')}
            />
            <ToggleInput
              form={form}
              name="sync_config.enable_yardi_work_requests"
              label={t('Sync Work Requests with Yardi')}
              description={t('Import Yardi work orders and send Grata work requests to Yardi')}
            />
            <ToggleInput
              form={form}
              name="sync_config.enable_yardi_send_welcome_email"
              label={t('Send Welcome Emails to Synced Residents')}
              description={t('Email residents added by the Yardi sync their welcome email')}
            />
            <ToggleInput
              form={form}
              name="sync_config.pms_sync_future_residents"
              label={t('Sync Future Residents from PMS')}
              description={t('Create Future residents from Yardi as pending users before they move in')}
            />
            <ToggleInput
              form={form}
              name="sync_config.pms_sync_pm_emails"
              label={t('Email PMS Sync Issues to Property Managers')}
              description={t('Email property managers the residents the PMS sync could not process (each resident at most once a day)')}
            />
            <ToggleInput
              form={form}
              name="sync_config.pms_sync_pets"
              label={t('Sync Pets with PMS Work Orders')}
              description={t('Send the work request "Has Pets" answer to the PMS and read it back from PMS work orders')}
            />
          </>
        )}

        {isEntrata && (
          <ToggleInput
            form={form}
            name="sync_config.enable_entrata_multi_building_property"
            label={t('Entrata Multi-Building Property')}
            description={t('Prefix unit numbers with the Entrata building name (for example A-101)')}
          />
        )}

        {showGxAcl && (
          <ToggleInput
            form={form}
            name="sync_config.enable_auto_gx_sync_acl"
            label={t('Automatic ICT Access Sync')}
            description={t('Sync access lists to the ICT controller automatically')}
          />
        )}

        {showSaltoAcl && (
          <ToggleInput
            form={form}
            name="sync_config.enable_auto_salto_sync_acl"
            label={t('Automatic Salto Access Sync')}
            description={t('Sync access lists to Salto automatically')}
          />
        )}
      </Flex>
    </SectionContainer>
  );
};

export default SyncConfigFormInputs;
